import { Component, inject, input, OnDestroy, OnInit, output } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { AutoAttendantHoliday, DateTimeSpan } from '../../../../shared/models/generated/ziroModels';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { HtmlInputType, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import { Observable, of, Subscription } from 'rxjs';
import { ZiroFormsModule } from '../../../../forms/ziro-forms.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../../forms/fields/select/ziro-select.component';
import { UiCallFlow } from '../teams-auto-attendants-call-flow.component';
import { isEqual } from 'lodash';
import { ZiroModalService } from '../../../../shared/services/ziro-modal.service';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { filter, switchMap } from 'rxjs/operators';
import { ButtonSizes, ButtonStyles, ZiroButtonComponent } from '../../../../button/button.component';
import { TeamsAutoAttendantEditContext } from '../../../teams-auto-attendant-edit.context';
import { ActivatedRoute } from '@angular/router';
import { TeamsAutoAttendantsHolidayResource } from '../../../resources/teams-auto-attendants-holiday.resource';
import { ToastService } from '../../../../shared/services/toast.service';
import { CreateHolidayModalComponent } from './create-holiday-modal/create-holiday-modal.component';
import { DatePipe } from '@angular/common';

export interface HolidayCallFlowFormData {
  name: string;
  holidaySchedule: ZiroSelectOption;
}

@Component({
  selector: 'ziro-holiday-call-flow-form',
  imports: [ZiroFormsModule, TranslateModule, ZiroButtonComponent, DatePipe],
  templateUrl: './holiday-call-flow-form.component.html',
  styleUrl: './holiday-call-flow-form.component.scss',
})
export class HolidayCallFlowFormComponent
  extends ZiroFormAbstractDirective<HolidayCallFlowFormData>
  implements OnInit, OnDestroy
{
  private _ziroModalService = inject(ZiroModalService);
  private _editContext = inject(TeamsAutoAttendantEditContext);
  private _route = inject(ActivatedRoute);
  private _autoAttendantHolidayResource = inject(TeamsAutoAttendantsHolidayResource);
  private _toastService = inject(ToastService);
  private _translateService = inject(TranslateService);

  allHolidays = input.required<AutoAttendantHoliday[]>();
  holidayCallFlow = input.required<UiCallFlow>();

  callFlowNameChange = output<string>();
  holidayListChange = output<AutoAttendantHoliday[]>();
  validityChange = output<boolean>();

  formConfig: ZiroFormConfig;
  private _subscriptions = new Subscription();
  private _callFlowWithDuplicateScheduleName = '';
  private _callFlowWithOverlappingScheduleName = '';

  get localHolidays(): AutoAttendantHoliday[] {
    return this._editContext.holidays();
  }

  get selectedDateRanges(): DateTimeSpan[] {
    const selected = this.formData?.holidaySchedule?.value as AutoAttendantHoliday | null;
    return selected?.dateTimeRanges ?? [];
  }

  ngOnInit(): void {
    const existingHoliday = this.holidayCallFlow().holiday;
    const maxCharacterLimit = 64;
    this.formConfig = {
      fields: {
        name: {
          label: () => 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.holiday.name.label',
          dataAutomation: 'holiday-name',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.TEXT,
          }),
          required: () => true,
          validation: [
            {
              validator: (name: string) => {
                const trimmed = name?.trim().toLowerCase();
                if (!trimmed) {
                  return ZiroFormsValidationState.VALID;
                }
                return trimmed.length > maxCharacterLimit
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID;
              },
              message: () =>
                'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.holiday.name.length.message',
            },
            {
              validator: (name: string) => {
                const trimmed = name?.trim().toLowerCase();
                if (!trimmed) {
                  return ZiroFormsValidationState.VALID;
                }
                const isDuplicate = this._editContext
                  .localHolidayCallFlows()
                  .some((flow) => flow.id !== this.holidayCallFlow().id && flow.name?.trim().toLowerCase() === trimmed);
                return isDuplicate ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
              },
              message: () =>
                'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.holiday_name.duplicate.message',
            },
          ],
        },
        holidaySchedule: {
          label: () => 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.holiday.schedule.label',
          dataAutomation: 'holiday-schedule-select',
          componentConfig: new ZiroSelectConfig({
            options: this.localHolidays.map((holiday) => ({
              label: holiday.name,
              value: holiday,
            })),
            hideClear: true,
          }),
          required: () => true,
          validation: [
            {
              validator: (option: ZiroSelectOption) => {
                const duplicateFlow = this._editContext
                  .localHolidayCallFlows()
                  .find((flow) => flow.id !== this.holidayCallFlow().id && flow.holiday?.id === option.value?.id);

                this._callFlowWithDuplicateScheduleName = duplicateFlow?.name;
                return duplicateFlow ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
              },
              message: () =>
                this._translateService.instant(
                  'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.holiday.duplicate.message',
                  { holidayCallFlowName: this._callFlowWithDuplicateScheduleName },
                ),
            },
            {
              validator: (option: ZiroSelectOption) => {
                const overlappingFlow = this._editContext.findOverlappingHolidayCallFlow(
                  this.holidayCallFlow().id,
                  option.value?.dateTimeRanges ?? [],
                );

                this._callFlowWithOverlappingScheduleName = overlappingFlow?.name;
                return overlappingFlow ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
              },
              message: () =>
                this._translateService.instant(
                  'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.holiday.schedule_overlap.message',
                  { holidayCallFlowName: this._callFlowWithOverlappingScheduleName },
                ),
            },
          ],
        },
      },
    };

    const matchedOption = existingHoliday
      ? (this.localHolidays
          .map((holiday) => ({ label: holiday.name, value: holiday }))
          .find((opt) => opt.value.id === existingHoliday.id) ?? null)
      : null;

    this.entitySource.next({
      name: this.holidayCallFlow().name ?? '',
      holidaySchedule: matchedOption,
    });

    const updateSub = this.ziroFormsUpdate$.subscribe((update) => {
      if (!isEqual(update.new, update.old)) {
        if (update.new?.name !== update.old?.name) {
          this.callFlowNameChange.emit(update.new?.name);
          this._editContext.updateHolidayCallFlowName(this.holidayCallFlow().id, update.new.name);
        }

        const oldHolidayId = (update.old?.holidaySchedule?.value as AutoAttendantHoliday)?.id;
        const newHoliday = update.new?.holidaySchedule?.value as AutoAttendantHoliday | null;
        if (newHoliday?.id !== oldHolidayId) {
          this._editContext.updateHolidaySchedule(this.holidayCallFlow().id, newHoliday);
        }
      }
      if (update.valid !== ZiroFormsValidationState.PENDING) {
        this.validityChange.emit(update.valid === ZiroFormsValidationState.VALID);
      }
    });
    this._subscriptions.add(updateSub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscriptions.unsubscribe();
  }

  openCreateHolidayModal(): void {
    const tenantId = this._route.snapshot.paramMap.get('tenantId');
    const options = {
      size: 'lg',
      bodyClass: CreateHolidayModalComponent,
      modalViewProperties: {
        title: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.holiday.create_modal.title',
        submitButtonLabel: 'tkey;global.button.save.text',
        icon: ZiroIcons.ADD,
        allHolidays: this.localHolidays,
        modalBodyClass: 'modal-body--full-height',
        submitFn: (holiday: AutoAttendantHoliday): Observable<AutoAttendantHoliday> => {
          const normalised: AutoAttendantHoliday = {
            ...holiday,
            dateTimeRanges: holiday.dateTimeRanges.map((range) => ({
              start: this._appendSeconds(range.start),
              end: this._appendSeconds(range.end),
            })),
          };
          return this._autoAttendantHolidayResource.createHoliday(tenantId, normalised).pipe(
            switchMap((ref) => {
              const created: AutoAttendantHoliday = { ...normalised, id: ref.id };
              this._toastService.pushSaveToast(
                'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.holiday.title',
                created.name,
                ZiroIcons.AUTO_ATTENDANT,
              );
              return of(created);
            }),
          );
        },
      },
    };

    this._ziroModalService
      .openDetailedModal(() => options.modalViewProperties, options)
      .pipe(filter((result): result is AutoAttendantHoliday => !!result?.id))
      .subscribe((newHoliday) => {
        const updated = [...this.localHolidays, newHoliday];
        this._editContext.holidays.set(updated);
        this._refreshSelectOptions(updated);
        this.holidayListChange.emit(updated);

        const newOption: ZiroSelectOption = { label: newHoliday.name, value: newHoliday };
        this.entitySource.next({ ...this.formData, holidaySchedule: newOption });
        this._editContext.updateHolidaySchedule(this.holidayCallFlow().id, newHoliday);
      });
  }

  openEditHolidayModal(): void {
    const selectedHoliday = this.formData?.holidaySchedule?.value as AutoAttendantHoliday | null;
    if (!selectedHoliday) {
      return;
    }
    const tenantId = this._route.snapshot.paramMap.get('tenantId');
    const options = {
      size: 'lg',
      bodyClass: CreateHolidayModalComponent,
      modalViewProperties: {
        title: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.holiday.create_modal.title',
        existingHoliday: { name: selectedHoliday.name, dateTimeRanges: selectedHoliday.dateTimeRanges },
        submitButtonLabel: 'tkey;global.button.save.text',
        icon: ZiroIcons.EDIT,
        allHolidays: this.localHolidays,
        modalBodyClass: 'modal-body--full-height',
        submitFn: (holiday: AutoAttendantHoliday): Observable<AutoAttendantHoliday> => {
          const normalised: AutoAttendantHoliday = {
            ...holiday,
            id: selectedHoliday.id,
            dateTimeRanges: holiday.dateTimeRanges.map((range) => ({
              start: this._appendSeconds(range.start),
              end: this._appendSeconds(range.end),
            })),
          };
          return this._autoAttendantHolidayResource.updateHoliday(tenantId, selectedHoliday.id, normalised).pipe(
            switchMap(() => {
              this._toastService.pushSaveToast(
                'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.holiday.title',
                normalised.name,
                ZiroIcons.AUTO_ATTENDANT,
              );
              return of(normalised);
            }),
          );
        },
      },
    };

    this._ziroModalService
      .openDetailedModal(() => options.modalViewProperties, options)
      .pipe(filter((result): result is AutoAttendantHoliday => !!result?.id))
      .subscribe((updatedHoliday) => {
        const updated = this.localHolidays.map((h) => (h.id === updatedHoliday.id ? updatedHoliday : h));
        this._editContext.holidays.set(updated);
        this._refreshSelectOptions(updated);
        this.holidayListChange.emit(updated);

        const updatedOption: ZiroSelectOption = { label: updatedHoliday.name, value: updatedHoliday };
        this.entitySource.next({ ...this.formData, holidaySchedule: updatedOption });
        this._editContext.updateHolidaySchedule(this.holidayCallFlow().id, updatedHoliday);
      });
  }

  triggerSubmitValidation(): void {
    this._setIsFormSubmitted(true);
    this.validateAllFields();
  }

  private _refreshSelectOptions(holidays: AutoAttendantHoliday[]): void {
    if (this.fieldChannels?.['holidaySchedule']) {
      this.fieldChannels['holidaySchedule'].componentConfigSource.next(
        new ZiroSelectConfig({ options: holidays.map((holiday) => ({ label: holiday.name, value: holiday })) }),
      );
    }
  }

  private _appendSeconds(dateTimeLocal: string): string {
    if (!dateTimeLocal || dateTimeLocal.length < 16) {
      return dateTimeLocal;
    }

    const [datePart, timePart] = dateTimeLocal.split('T');
    const [hours, minutes] = timePart.split(':').map(Number);

    const roundedMinutes = Math.floor(minutes / 15) * 15;
    const hh = String(hours).padStart(2, '0');
    const mm = String(roundedMinutes).padStart(2, '0');

    return `${datePart}T${hh}:${mm}:00`;
  }

  protected submit(): Observable<any> {
    return of(null);
  }

  protected readonly ButtonStyles = ButtonStyles;
  protected readonly ZiroIcons = ZiroIcons;
  protected readonly ButtonSizes = ButtonSizes;
}
